import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Radio,
  Typography,
  CircularProgress,
  Box,
  Chip,
  Alert,
  Snackbar
} from '@mui/material';
import { AccountCircle } from '@mui/icons-material';

const styles = {
  dialogTitle: {
    textAlign: 'center',
    fontWeight: 'bold',
    fontSize: '1.2rem',
    color: 'white',
    backgroundColor: '#19438C',
    py: 2
  },
  listItemButton: {
    px: 2,
    py: 1.5,
    '&:hover': { backgroundColor: 'rgba(107, 197, 208, 0.1)' },
    opacity: 1
  },
  disabledListItemButton: {
    px: 2,
    py: 1.5,
    '&:hover': { backgroundColor: 'inherit' },
    opacity: 0.7
  },
  dialogActions: {
    justifyContent: 'flex-end',
    alignItems: 'center',
    p: 2,
    borderTop: '1px solid #e0e0e0'
  },
  cancelButton: {
    mr: 2,
    color: '#f44336',
    fontWeight: 'bold'
  },
  confirmButton: {
    backgroundColor: '#19438C',
    color: 'white',
    '&:hover': { backgroundColor: '#5ab4be' },
    minWidth: 120
  }
};

const ModalReasignarProcurador = ({
  open,
  onClose,
  tramite,
  procuradores,
  onReasignacionExitosa
}) => {
  const [procuradorSeleccionado, setProcuradorSeleccionado] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [procuradorActualMapeado, setProcuradorActualMapeado] = useState(null);

  // Mapear el procurador actual al abrir el modal
  useEffect(() => {
    if (open && procuradores.length > 0) {
      setProcuradorSeleccionado(null);
      setError(null);
      let procuradorMapeado = null;

      // Buscar por ID
      if (tramite?.procurador_id != null) {
        procuradorMapeado = procuradores.find(
          p => String(p.id) === String(tramite.procurador_id)
        );
      }

      // Si no lo encontró, buscar por nombre
      if (!procuradorMapeado && tramite?.procurador) {
        procuradorMapeado = procuradores.find(
          p => p.nombre.trim().toLowerCase() === tramite.procurador.trim().toLowerCase()
        );
      }

      // Si sigue sin encontrar, crear un objeto con la información disponible
      if (!procuradorMapeado) {
        procuradorMapeado = {
          id: tramite?.procurador_id || null,
          nombre: tramite?.procurador || 'Procurador actual'
        };
      }

      setProcuradorActualMapeado(procuradorMapeado);
    }
  }, [open, tramite, procuradores]);

  const handleSeleccionar = (procurador) => {
    // No hacer nada si es el procurador actual
    if (procuradorActualMapeado && 
        (procurador.id === procuradorActualMapeado.id || 
         procurador.nombre === procuradorActualMapeado.nombre)) {
      return;
    }
    setProcuradorSeleccionado(procurador);
  };

  const handleCerrar = () => {
    setProcuradorSeleccionado(null);
    setError(null);
    setSuccess(null);
    onClose();
  };

  const handleReasignar = async () => {
    if (!tramite || !procuradorSeleccionado) return;

    try {
      setCargando(true);
      setError(null);

      const response = await fetch(
        'https://bei360.injupemp.gob.hn:8444/api/tramites-administrativos/reasignar/',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            tramite_id: tramite.id,
            nuevo_procurador_id: procuradorSeleccionado.id
          })
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Error al reasignar el procurador');
      }

      const result = await response.json();
      if (onReasignacionExitosa) {
        onReasignacionExitosa(result);
      }

      setSuccess('Procurador reasignado exitosamente');
      setTimeout(() => {
        handleCerrar();
      }, 1500);
    } catch (err) {
      setError(err.message);
    } finally {
      setCargando(false);
    }
  };

  // Función para verificar si un procurador es el actual
  const esProcuradorActual = (procurador) => {
    if (!procuradorActualMapeado) return false;
    
    // Comparar por ID si existe
    if (procuradorActualMapeado.id && procurador.id === procuradorActualMapeado.id) {
      return true;
    }
    
    // Comparar por nombre si no hay ID
    if (!procuradorActualMapeado.id && procurador.nombre === procuradorActualMapeado.nombre) {
      return true;
    }
    
    return false;
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleCerrar}
        maxWidth={false}
        sx={{
          '& .MuiDialog-paper': {
            width: '500px',
            maxWidth: '100%',
            maxHeight: '90vh',
            height: '500px',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={styles.dialogTitle}>
          Reasignar Procurador - Trámite #{tramite?.id || ''}
        </DialogTitle>

        <DialogContent
          sx={{
            flex: 1,
            overflowY: 'auto',
            pt: 3,
            pb: 1,
            minHeight: '200px'
          }}
        >
         
          <Typography variant="body1" sx={{ mb: 2 }}>
            Seleccione el nuevo procurador:
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
            <List disablePadding>
              {procuradores.length === 0 ? (
                <Box textAlign="center" p={3}>
                  <Typography color="textSecondary">
                    No hay procuradores disponibles
                  </Typography>
                </Box>
              ) : (
                procuradores.map((procurador) => {
                  const isCurrent = esProcuradorActual(procurador);
                  const isSelected = procuradorSeleccionado?.id === procurador.id;

                  return (
                    <ListItem key={procurador.id} disablePadding>
                      <ListItemButton
                        onClick={() => handleSeleccionar(procurador)}
                        disabled={isCurrent || cargando}
                        sx={isCurrent ? styles.disabledListItemButton : styles.listItemButton}
                      >
                        <Radio
                          checked={isSelected}
                          disabled={isCurrent}
                          sx={{
                            color: isCurrent ? 'grey.500' : '#19438C',
                            '&.Mui-checked': {
                              color: isCurrent ? 'grey.500' : '#d81c1cff'
                            }
                          }}
                        />
                        <AccountCircle
                          sx={{
                            fontSize: 40,
                            color: isCurrent ? 'grey.500' : '#19438C',
                            marginRight: 2,
                            ...(isSelected && !isCurrent && {
                              color: '#19438C'
                            })
                          }}
                        />
                        <ListItemText
                          primary={
                            <>
                              {(procurador.nombre || '').toUpperCase()} {(procurador.apellido || '').toUpperCase()}
                              {isCurrent && (
                                <Chip
                                  label="Actual"
                                  size="small"
                                  sx={{
                                    ml: 1,
                                    backgroundColor: 'grey.300',
                                    color: 'grey.700',
                                    fontSize: '0.65rem'
                                  }}
                                />
                              )}
                            </>
                          }
                          primaryTypographyProps={{
                            fontWeight: 'medium',
                            color: isCurrent ? 'text.disabled' : 'text.primary'
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                  );
                })
              )}
            </List>
          </Box>
        </DialogContent>

        <DialogActions sx={styles.dialogActions}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', width: '100%' }}>
            <Button
              onClick={handleCerrar}
              disabled={cargando}
              sx={styles.cancelButton}
            >
              Cancelar
            </Button>

            <Button
              variant="contained"
              onClick={handleReasignar}
              disabled={
                cargando ||
                !procuradorSeleccionado ||
                esProcuradorActual(procuradorSeleccionado)
              }
              sx={styles.confirmButton}
            >
              {cargando ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'Confirmar Reasignación'
              )}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Snackbar de error */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>

      {/* Snackbar de éxito */}
      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity="success" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ModalReasignarProcurador;